// components/config/ConfigFormModal.tsx
import React, { useEffect, useState } from 'react';
import './ConfigFormModal.less';
import { Config, BlindLevel } from '../../types/config';
import { generateBlindStructure } from '../utils/generateBlindStructure';

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    existing?: Config;
}

function SortableItem({
                          id,
                          children,
                      }: {
    id: string;
    children: React.ReactNode;
}) {
    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className="flex items-center gap-2 mb-2">
            <span
                {...attributes}
                {...listeners}
                className="cursor-move text-gray-500 px-2 select-none"
                title="Ziehen, um Reihenfolge zu ändern"
            >
                ≡
            </span>
            {children}
        </div>
    );
}

export default function ConfigFormModal({ isOpen, onClose, onSuccess, existing }: Props) {
    const [name, setName] = useState('');
    const [blinds, setBlinds] = useState<BlindLevel[]>([]);
    const [error, setError] = useState('');

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        setName(existing?.name || '');
        setBlinds(existing?.blinds || []);
    }, [existing]);

    const handleAddBlind = () => {
        setBlinds([...blinds, { smallBlind: 0, bigBlind: 0, ante: 0, duration: 10 }]);
    };

    const handleChangeBlind = (
        index: number,
        key: 'smallBlind' | 'bigBlind' | 'ante' | 'duration',
        value: number
    ) => {
        const updated = [...blinds];
        updated[index] = {
            ...updated[index],
            [key]: value,
        };
        setBlinds(updated);
    };

    const handleRemoveBlind = (index: number) => {
        setBlinds(blinds.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem('userToken');
        const method = existing ? 'PUT' : 'POST';
        const url = existing ? `/api/user/configs/${existing._id}` : '/api/user/configs';

        const res = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ name, blinds }),
        });

        if (!res.ok) {
            const data = await res.json();
            setError(data.message || 'Fehler beim Speichern');
            return;
        }

        onSuccess();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="config-form-modal fixed inset-0 flex justify-center items-center z-50">
            <div className="config-form-wrapper p-6 rounded shadow-lg w-full max-w-5xl overflow-y-auto max-h-[90vh]">
                <h2 className="text-2xl font-bold mb-6">
                    {existing ? 'Config bearbeiten' : 'Neue Config'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block mb-1 font-semibold">Config Name</label>
                        <input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="w-full border px-3 py-2"
                            placeholder="Turniername oder Beschreibung"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block mb-1 font-semibold">Blindstruktur generieren</label>
                        <div className="flex gap-2">
                            <select
                                className="border px-3 py-2"
                                value=""
                                onChange={(e) => {
                                    const selected = e.target.value as 'turbo' | 'standard' | 'deepstack';
                                    if (selected) {
                                        const generated = generateBlindStructure(selected);
                                        setBlinds(generated);
                                    }
                                }}
                            >
                                <option value="">-- Variante wählen --</option>
                                <option value="turbo">Turbo (10 min)</option>
                                <option value="standard">Standard (20 min)</option>
                                <option value="deepstack">Deepstack (30 min)</option>
                            </select>
                            <button
                                type="button"
                                className="text-sm text-blue-600 underline"
                                onClick={() => setBlinds([])}
                            >
                                Zurücksetzen
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold">Blindlevels</span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleAddBlind}
                                    className="bg-green-600 text-white text-sm px-3 py-1 rounded"
                                >
                                    + Blindlevel
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setBlinds([
                                            ...blinds,
                                            { type: 'break', smallBlind: 0, bigBlind: 0, ante: 0, duration: 5 },
                                        ])
                                    }
                                    className="bg-yellow-600 text-white text-sm px-3 py-1 rounded"
                                >
                                    + Pause
                                </button>
                            </div>
                        </div>

                        {blinds.length > 0 && (
                            <div className="grid grid-cols-5 gap-3 text-sm font-semibold mb-2 ml-8">
                                <div>Small Blind</div>
                                <div>Big Blind</div>
                                <div>Ante</div>
                                <div>Dauer (Minuten)</div>
                                <div></div>
                            </div>
                        )}

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={({ active, over }) => {
                                if (active.id !== over?.id) {
                                    const oldIndex = parseInt(String(active.id));
                                    const newIndex = parseInt(String(over?.id ?? '0'));
                                    setBlinds(arrayMove(blinds, oldIndex, newIndex));
                                }
                            }}
                        >
                            <SortableContext
                                items={blinds.map((_, index) => index.toString())}
                                strategy={verticalListSortingStrategy}
                            >
                                {blinds.map((blind, index) => (
                                    <SortableItem key={index} id={index.toString()}>
                                        {blind.type === 'break' ? (
                                            <div className="grid grid-cols-5 gap-3 items-center flex-1 text-gray-600 italic">
                                                <div className="col-span-3">Pause</div>
                                                <input
                                                    type="number"
                                                    placeholder="Dauer"
                                                    value={blind.duration}
                                                    onChange={e =>
                                                        handleChangeBlind(index, 'duration', Number(e.target.value))
                                                    }
                                                    className="border px-2 py-1"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveBlind(index)}
                                                    className="text-red-600 text-sm"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-5 gap-3 items-center flex-1">
                                                <input
                                                    type="number"
                                                    placeholder="Small Blind"
                                                    value={blind.smallBlind}
                                                    onChange={e =>
                                                        handleChangeBlind(index, 'smallBlind', Number(e.target.value))
                                                    }
                                                    className="border px-2 py-1"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Big Blind"
                                                    value={blind.bigBlind}
                                                    onChange={e =>
                                                        handleChangeBlind(index, 'bigBlind', Number(e.target.value))
                                                    }
                                                    className="border px-2 py-1"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Ante"
                                                    value={blind.ante}
                                                    onChange={e =>
                                                        handleChangeBlind(index, 'ante', Number(e.target.value))
                                                    }
                                                    className="border px-2 py-1"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Dauer"
                                                    value={blind.duration}
                                                    onChange={e =>
                                                        handleChangeBlind(index, 'duration', Number(e.target.value))
                                                    }
                                                    className="border px-2 py-1"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveBlind(index)}
                                                    className="text-red-600 text-sm"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )}
                                    </SortableItem>
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>

                    {error && <div className="text-red-600 text-sm">{error}</div>}

                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="border px-4 py-2">
                            Abbrechen
                        </button>
                        <button type="submit" className="button px-4 py-2">
                            {existing ? 'Speichern' : 'Erstellen'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
